import type { ComponentProps } from "react";

import FindReplaceIcon from "@mui/icons-material/FindReplace";
import {
  Box,
  Checkbox,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DateField } from "components/Forms/DateField";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { MultipleFacilityPicker } from "components/MultipleFacilityPicker";
import { TagPicker } from "components/TagPicker";
import { getNamingFormatHelpText, KnownSubmissionDocumentTypes } from "encamp-shared/src/documents";
import {
  TagType,
  type DocumentType,
  type FacilityDocumentInput,
  type Issue,
  type MultipleFacilityPickerFragment,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { type Control, Controller, useWatch } from "react-hook-form";
import { documentTypeToLabel } from "util/constants";

import type { DocumentFormData, ExistingDefaultDocument } from "../types";

import { DocumentDialogContainer } from "../../shared";

type DialogContainerProps = Omit<ComponentProps<typeof DocumentDialogContainer>, "children">;

type FormStepProps = {
  dialogProps: DialogContainerProps;
  dialogHeight: number;
  previewUrl?: string;
  previewLoading: boolean;
  displayPreview: boolean;
  fileExtension?: string;
  filenameStandardization: {
    shouldShowCheckbox: boolean;
    keepOriginalFilename: boolean;
    setKeepOriginalFilename: (keepOriginal: boolean) => void;
  };
  shouldFieldsBeDisabled: boolean;
  control: Control<DocumentFormData>;
  issues?: Issue[];
  isInvalidSds: boolean;
  hmbpWarningMessage?: string;
  isReadOnly: boolean;
  onResetToUploadStep: () => void;
  disableDocumentTypeSelection: boolean;
  documentPickerOptions: { label: string; value: DocumentType }[];
  activitySelectItems?:
    | {
        display: string;
        value: string | null;
      }[]
    | undefined;
  showTagPicker?: boolean;
  showFacilityPicker?: boolean;
  tenantId: string;
  existingDefaults?: ExistingDefaultDocument[];
  editingDocumentId?: string;
};

export function FormStep({
  dialogProps,
  dialogHeight,
  previewUrl,
  previewLoading,
  displayPreview,
  fileExtension,
  filenameStandardization,
  shouldFieldsBeDisabled,
  control,
  issues = [],
  isInvalidSds,
  hmbpWarningMessage,
  isReadOnly,
  onResetToUploadStep,
  disableDocumentTypeSelection,
  documentPickerOptions,
  activitySelectItems,
  showTagPicker,
  showFacilityPicker,
  tenantId,
  existingDefaults,
  editingDocumentId,
}: FormStepProps) {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const watchedDocumentType = useWatch({ control, name: "documentType" });
  const fileSizeIssue = issues.find((issue) => issue.key === "fileSize");

  return (
    <DocumentDialogContainer
      {...dialogProps}
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          height: dialogHeight,
        },
      }}
      contentSx={{ pt: 0 }}
      previewUrl={displayPreview ? previewUrl : undefined}
      previewLoading={previewLoading}
      fileExtension={fileExtension}
      showPreviewBoxIfFailed
      formColumnSize={{
        xs: 4,
      }}
      previewColumnSize={{
        xs: 8,
      }}
    >
      <Stack gap={isReadOnly ? theme.spacing(3) : theme.spacing(1)} sx={{ flex: 1, width: "100%" }}>
        <Grid container spacing={2} columns={12}>
          <Grid item xs={11}>
            {filenameStandardization.shouldShowCheckbox && (
              <Typography variant="body2" color="text.secondary" sx={{ pb: theme.spacing(1) }}>
                Filename will be standardized to match the format "
                {getNamingFormatHelpText(tenantId)}"
              </Typography>
            )}

            {filenameStandardization.shouldShowCheckbox && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filenameStandardization.keepOriginalFilename}
                    onChange={(event) => {
                      filenameStandardization.setKeepOriginalFilename(event.target.checked);
                    }}
                    disabled={shouldFieldsBeDisabled}
                  />
                }
                label="Keep original file name"
              />
            )}
          </Grid>
          <Grid item xs={11} mt={theme.spacing(1)}>
            <FormTextField
              name="title"
              label="Filename *"
              control={control}
              sx={{ marginTop: theme.spacing(1) }}
              disabled={shouldFieldsBeDisabled}
            />
          </Grid>
          {!shouldFieldsBeDisabled && !isReadOnly && (
            <Grid
              item
              xs={1}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingBottom: theme.spacing(2.5),
              }}
            >
              <IconButton
                onClick={() => {
                  onResetToUploadStep();
                }}
              >
                <FindReplaceIcon color="primary" fontSize="medium" />
              </IconButton>
            </Grid>
          )}
        </Grid>

        <div style={{ display: "none" }}>
          <FormTextField
            name="fileExtension"
            hideErrors
            label="Format"
            control={control}
            disabled={shouldFieldsBeDisabled}
          />
        </div>

        <div style={{ display: "none" }}>
          <FormTextField
            name="storageLink"
            hideErrors
            label="Storage Link"
            control={control}
            disabled={shouldFieldsBeDisabled}
          />
        </div>

        {fileSizeIssue && (
          <Typography color="error" sx={{ mt: 0, pt: 0, mb: theme.spacing(1) }}>
            {fileSizeIssue.message}
          </Typography>
        )}

        <FormAutocomplete
          label="Document Type *"
          control={control}
          name="documentType"
          autocompleteItems={documentPickerOptions}
          disabled={shouldFieldsBeDisabled || disableDocumentTypeSelection}
        />

        {isInvalidSds && (
          <Typography color="error" sx={{ mt: 0, pt: 0, mb: theme.spacing(2) }}>
            Safety Data Sheets must be in PDF format
          </Typography>
        )}

        {hmbpWarningMessage && (
          <Typography color="error" sx={{ mt: 0, pt: 0, mb: theme.spacing(2) }}>
            {hmbpWarningMessage}
          </Typography>
        )}

        <DateField
          name="authoredAt"
          label="Date Authored"
          control={control}
          disabled={shouldFieldsBeDisabled}
        />

        <FormTextField
          name="description"
          label="Description"
          control={control}
          disabled={shouldFieldsBeDisabled}
          textFieldProps={{
            multiline: true,
            minRows: 1,
            maxRows: 6,
          }}
        />

        {isStaff && (
          <Controller
            name="currentSubmissionDocument"
            control={control}
            render={({ field }) => {
              const isSubmissionType = KnownSubmissionDocumentTypes.includes(
                watchedDocumentType as DocumentType
              );
              if (!isSubmissionType) return <></>;

              const existingDefault = existingDefaults?.find(
                (d) => d.documentType === watchedDocumentType && d.id !== editingDocumentId
              );
              const showWarning = field.value === true && existingDefault != null;

              return (
                <Box>
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        checked={field.value === true}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={shouldFieldsBeDisabled}
                      />
                    }
                    label={
                      <Stack>
                        <Typography variant="body2">
                          Current submission document (Staff Only)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          This document will be automatically attached to future reports
                        </Typography>
                      </Stack>
                    }
                  />
                  {showWarning && (
                    <Typography variant="body2" color="error" sx={{ ml: 4, mt: 0.5 }}>
                      This will replace &ldquo;{existingDefault.title}&rdquo; as the current{" "}
                      {documentTypeToLabel(existingDefault.documentType as DocumentType)} submission
                      document
                    </Typography>
                  )}
                </Box>
              );
            }}
          />
        )}

        {activitySelectItems && (
          <FormSelect
            name="activityId"
            label="Activity"
            control={control}
            selectItems={activitySelectItems}
            disabled={shouldFieldsBeDisabled}
          />
        )}

        {showTagPicker && (
          <Box
            sx={{
              mb: theme.spacing(4),
            }}
          >
            <Controller
              name="documentTags"
              control={control}
              render={({ field, fieldState }) => (
                <TagPicker
                  {...field}
                  {...fieldState}
                  tagType={TagType.Document}
                  label="Tags"
                  disabled={shouldFieldsBeDisabled}
                />
              )}
            />
          </Box>
        )}

        <Controller
          name="facilities"
          control={control}
          render={({ field, fieldState }) => (
            <MultipleFacilityPicker
              {...field}
              {...fieldState}
              sx={{
                display: showFacilityPicker ? "block" : "none",
              }}
              value={
                field.value?.map((facility) => ({
                  id: facility.id ?? "",
                  name: facility.name ?? "",
                })) ?? []
              }
              defaultSearchTerm={`tenantId:${tenantId}`}
              label="Facilities"
              onChange={(facilities: MultipleFacilityPickerFragment[] | null) => {
                const mappedFacilities: FacilityDocumentInput[] =
                  facilities?.map((facility) => ({
                    id: facility.id,
                    name: facility.name ?? undefined,
                  })) ?? [];
                field.onChange(mappedFacilities);
              }}
              disabled={shouldFieldsBeDisabled}
            />
          )}
        />
      </Stack>
    </DocumentDialogContainer>
  );
}
